import CloseIcon from "@heroicons/react/outline/XIcon";
import firebase from "firebase/app";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";

import { AnalyticsEventEnum } from "@/consts/AnalyticsEventEnum";
import { DialogEnum } from "@/consts/DialogEnum";
import { RouteName } from "@/consts/RouteName";
import { StandardConfirmDialogView } from "@/features/common/components/layout/StandardConfirmDialogView";
import { useSignInHelper } from "@/features/user/hooks/signIn/useSignInHelper";
import { getUserProfilePath } from "@/helpers/common/path";

const AuthenticationView = dynamic(
  () =>
    import("@/features/auth/AuthenticationView").then(
      (mod) => mod.AuthenticationView,
    ),
  { ssr: false },
);

export enum SensitiveContentDialogViewEnum {
  Visitor = "VISITOR",
  Login = "LOGIN",
  Under18 = "UNDER_18",
  Setting = "SETTING",
}

export type SensitiveContentDialogViewProps = {
  writerAccountName: string;
  initialView: SensitiveContentDialogViewEnum;
  channelName?: string;
  postTitle?: string;
  onClose: () => void | Promise<void>;
};

const SensitiveContentDialogView = ({
  writerAccountName,
  initialView,
  channelName,
  postTitle,
  onClose: onCloseDialog,
}: SensitiveContentDialogViewProps) => {
  const { t } = useTranslation("common");
  const router = useRouter();
  const [view, setView] = useState<SensitiveContentDialogViewEnum>(initialView);
  const { authInProgress } = useSignInHelper();

  useEffect(() => {
    setView(initialView);
  }, [initialView]);

  const currentType = !!channelName ? "channel" : "post";

  const onClose = async () => {
    firebase
      .analytics()
      .logEvent(AnalyticsEventEnum.SensitiveContentDialogClose);

    onCloseDialog();

    /* will close post dialog if open instead of redirecting to user profile page */
    if (!!router.query.preview) {
      const { preview, ...query } = router.query;
      router.push({ pathname: router.pathname, query }, undefined, {
        shallow: true,
      });
    } else {
      router.push(getUserProfilePath(writerAccountName));
    }
  };

  if (view === SensitiveContentDialogViewEnum.Visitor) {
    const onConfirm = () => {
      firebase
        .analytics()
        .logEvent(AnalyticsEventEnum.SensitiveContentDialogLogin);
      setView(SensitiveContentDialogViewEnum.Login);
    };
    return (
      <StandardConfirmDialogView
        data-test-id="SensitiveContentDialogView-visitorView"
        className="p-10"
        title={t("common:enterSensitiveContentFlow.visitor.title")}
        message={t("common:enterSensitiveContentFlow.visitor.message", {
          type: t(`common:enterSensitiveContentFlow.${currentType}`),
        })}
        confirmLabel={t("common:enterSensitiveContentFlow.visitor.confirm")}
        cancelLabel={t("common:enterSensitiveContentFlow.visitor.cancel")}
        onConfirm={onConfirm}
        onCancel={onClose}
      />
    );
  }

  if (view === SensitiveContentDialogViewEnum.Under18) {
    return (
      <StandardConfirmDialogView
        className="p-10"
        hideConfirmButton={true}
        title={t("common:enterSensitiveContentFlow.under18.title")}
        message={t("common:enterSensitiveContentFlow.under18.message", {
          name: channelName ?? postTitle ?? "",
          type: t(`common:enterSensitiveContentFlow.${currentType}`),
        })}
        cancelLabel={t("common:enterSensitiveContentFlow.under18.cancel")}
        onCancel={onClose}
      />
    );
  }

  if (view === SensitiveContentDialogViewEnum.Setting) {
    {
      const onConfirm = async () => {
        firebase
          .analytics()
          .logEvent(AnalyticsEventEnum.SensitiveContentDialogGoToMySettings);
        await router.push(RouteName.MySettingsProfile);
        onCloseDialog();
      };

      return (
        <StandardConfirmDialogView
          className="p-10"
          title={t("common:enterSensitiveContentFlow.setting.title")}
          message={t("common:enterSensitiveContentFlow.setting.message", {
            name: channelName ?? postTitle ?? "",
            type: t(`common:enterSensitiveContentFlow.${currentType}`),
          })}
          confirmLabel={t("common:enterSensitiveContentFlow.setting.confirm")}
          cancelLabel={t("common:enterSensitiveContentFlow.setting.cancel")}
          onConfirm={onConfirm}
          onCancel={onClose}
        />
      );
    }
  }

  const onCloseLoginDialog = () => {
    firebase
      .analytics()
      .logEvent(AnalyticsEventEnum.SensitiveContentDialogBackToVisitor);
    setView(SensitiveContentDialogViewEnum.Visitor);
  };

  return (
    <div className="relative p-6 pb-0">
      <div className="absolute top-10 right-10">
        <button
          onClick={onCloseLoginDialog}
          className="focus:outline-none disabled:cursor-not-allowed w-6 h-6"
          disabled={authInProgress}
        >
          <CloseIcon className="text-gray-400" />
        </button>
      </div>

      <AuthenticationView
        onCloseDialog={() => {}}
        dialogEnum={DialogEnum.Login}
      />
    </div>
  );
};

export default SensitiveContentDialogView;
